import {
  disableFSAForEnvironment,
  enableFSAForEnvironment,
  EnvironmentTypes,
  SearchConnectionsResponse
} from '~/api';
import { updateFeature } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ToastContextValue } from '~/components/ui/Toast';
import logger from '~/utils/logger';
import { EnvironmentPagePath } from '~/components/AppRouter/utils.ts';

const getTranslationKey = (translationKey: string) =>
  `fsa_onboarding.${translationKey}`;

const getTotalActiveConnections = (
  envType: EnvironmentTypes,
  data?: SearchConnectionsResponse
) => {
  if (!data) return 0;
  const isProdEnv = envType === EnvironmentTypes.PRODUCTION;
  return isProdEnv ? data.totalSize : data.totalSize - 1;
};

const enableFSAWithSuccessToast = async ({
  envId,
  toaster,
  intl
}: {
  envId: string;
  toaster: ToastContextValue;
  intl: any;
}) => {
  await enableFSAForEnvironment(envId);
  updateFeature(FeatureFlagKeys.FULL_STACK_AUTHENTICATION, true);
  toaster.toast().success({
    description: getLocalizedString(getTranslationKey('enable_fsa_success'), {
      intl
    })
  });
};

const disableFSAWithSuccessToast = async ({
  toaster,
  intl,
  toastMessage
}: {
  toaster: ToastContextValue;
  intl: any;
  toastMessage?: string;
}) => {
  await disableFSAForEnvironment();
  updateFeature(FeatureFlagKeys.FULL_STACK_AUTHENTICATION, false);
  toaster.toast().success({
    description:
      toastMessage ||
      getLocalizedString(getTranslationKey('disable_fsa_success'), {
        intl
      })
  });
};

const logAndToastFSAError = ({
  error,
  toaster,
  intl
}: {
  error: Error;
  toaster: ToastContextValue;
  intl: any;
}) => {
  logger.error(error);
  toaster.toast().error({
    description: getLocalizedString(getTranslationKey('enable_fsa_failure'), {
      intl
    })
  });
};

const isOnEnvironmentQuickStart = (): boolean => {
  const lastSegment = window.location.pathname.split('/').filter(Boolean).pop();

  return `/${lastSegment}` === EnvironmentPagePath.ENVIRONMENT_QUICK_START;
};

const FSA_FEATURES_TRANSLATION_KEYS = [
  `fsa_onboarding.fsa_feature_1`,
  `fsa_onboarding.fsa_feature_2`,
  `fsa_onboarding.fsa_feature_3`,
  `fsa_onboarding.fsa_feature_4`,
  `fsa_onboarding.fsa_feature_5`
];

const FSA_DEACTIVATE_FEATURES_TRANSLATION_KEYS = [
  `fsa_onboarding.fsa_deactivate_feature_1`,
  `fsa_onboarding.fsa_feature_2`,
  `fsa_onboarding.fsa_feature_3`,
  `fsa_onboarding.fsa_feature_4`,
  `fsa_onboarding.fsa_feature_5`
];

export {
  FSA_FEATURES_TRANSLATION_KEYS,
  FSA_DEACTIVATE_FEATURES_TRANSLATION_KEYS,
  getTotalActiveConnections,
  getTranslationKey,
  enableFSAWithSuccessToast,
  disableFSAWithSuccessToast,
  logAndToastFSAError,
  isOnEnvironmentQuickStart
};
